import React from 'react';
import { View, Platform } from 'react-native';
import styled, { css } from 'styled-components/native';

import usePrefersReducedMotion from 'app/src/hooks/usePrefersReducedMotion';
import colors from 'app/src/styles/colors';
import { duration, easing, getStaggerDelay } from 'app/src/styles/motion';

const animationMixin = css`
  ${props => props.shouldAnimate && Platform.OS === 'web' && css`
    animation: fadeReveal ${duration.normal}ms ${easing.reveal} ${props.animationDelay || 0}ms both;
  `}
`;

/**
 * Card component for elevated content sections.
 * Use to group related content with visual distinction.
 *
 * Props:
 *   - elevated: Use stronger elevation (parchment background)
 *   - bordered: Use border instead of background change
 *   - animate: Enable fade-reveal animation (default: true)
 *   - staggerIndex: Index for staggered list animations
 */
const CardContainer = styled(View)`
  background-color: ${colors.backgroundAlt};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  ${animationMixin}
`;

/**
 * Card with stronger elevation (parchment background)
 */
const ElevatedCardContainer = styled(View)`
  background-color: ${colors.card};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  ${animationMixin}
`;

/**
 * Card with border instead of background change
 */
const BorderedCardContainer = styled(View)`
  background-color: ${colors.background};
  border-width: 1px;
  border-color: ${colors.border};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  ${animationMixin}
`;

export const Card = ({ children, elevated, bordered, style, animate = true, staggerIndex }) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const shouldAnimate = animate && Platform.OS === 'web' && !prefersReducedMotion;
  const animationDelay = staggerIndex !== undefined ? getStaggerDelay(staggerIndex) : 0;

  const animationProps = {
    shouldAnimate,
    animationDelay,
    style,
  };

  if (elevated) {
    return <ElevatedCardContainer {...animationProps}>{children}</ElevatedCardContainer>;
  }
  if (bordered) {
    return <BorderedCardContainer {...animationProps}>{children}</BorderedCardContainer>;
  }
  return <CardContainer {...animationProps}>{children}</CardContainer>;
};

/**
 * Section component for adding vertical rhythm between content blocks
 */
export const Section = styled(View)`
  margin-bottom: 32px;
`;

/**
 * Prose container for text-heavy content with optimal line length
 */
export const Prose = styled(View)`
  max-width: 65ch;
`;

export default Card;
